import { FilterPills, nonEmptyPills } from '@/components/FilterPills';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSetSearchParam } from '@/lib/searchParams';
import { cn } from '@/lib/utils';
import type {
    BrandId,
    CandidateGate,
    DocumentCoverage,
    EquipmentTypeId,
    ModelDocTag,
    ModelDocument,
    ModelDocumentCandidate,
    ModelDocumentId,
    ModelDocumentLink,
    SpaceId,
} from '@rock/types';
import { DOC_TAG_LABELS, ModelDocTagSchema } from '@rock/types';
import {
    countPagesInRanges,
    formatPageRanges,
    includesAppVisibleTag,
    isManualishClassification,
    suggestedTags,
    supersessionRefusal,
} from '@rock/manuals';
import { Ban, ChevronRight, ExternalLink, MoreHorizontal, RotateCcw, Scissors } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { equipmentTypeFamiliesState, modelDocumentState } from '@/state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { TrailList } from './CandidateTrail';
import { publishedTags } from './documentEdits';
import {
    type CoverageDraft,
    type DocumentScope,
    type PublishScope,
    coverageDraftActive,
    coverageDraftCoversModel,
    coverageDraftFromExtraction,
    coverageSuggestions,
    documentScopeError,
    mergeScopes,
} from './coverageDraft';
import {
    type ApplyTrim,
    DocumentForm,
    DocumentLink,
    LanguageAnalysisBadge,
    PdfPreview,
    type RestoreTrim,
    TrimPdfDialog,
    canAnalyzeLanguages,
    describeDocumentDates,
    describeUrl,
} from './documentShared';

/** What the coverage section needs to know about the model being published on. */
export interface CoverageContext {
    brandId: BrandId | undefined;
    spaceId: SpaceId;
    typeId: EquipmentTypeId;
    /** Match key for "does the rule on screen actually reach this model?" — see `coverageDraftCoversModel`. */
    modelCanonical: string;
    /** The same model as the reviewer reads it; only ever shown, never matched on. */
    modelDisplay: string;
}

export interface CandidatesCardProps {
    candidates: ModelDocumentCandidate[];
    /** The model number under review — the card header states it, so a candidate is judged against it. */
    modelNumber: string;
    /**
     * The model's CONFIRMED document links (`model.documents`). Confirmed candidates leave the review
     * list (their document row above IS the record), and confirmed-ness is derived from the model doc —
     * not the candidate's `disposition` alone — so candidates whose bytes went live another way (a
     * migration, a re-observation of already-published bytes) leave the list too. Links suffice: only
     * presence by id matters here.
     */
    documents: Partial<Record<ModelDocumentId, ModelDocumentLink>>;
    /** The publishing model's brand/space/type — drives the coverage section. */
    coverageContext: CoverageContext;
    saving: boolean;
    /**
     * Confirm these staged bytes under the human-chosen tags (works on ANY verdict — the force-accept).
     * `notify` is the footer toggle: false publishes the manual without announcing it to affected owners.
     * `publish` carries the brands and types the document is for, whatever family rule survived the
     * reviewer's pruning, if any, and whether the model under review is linked at all.
     */
    onPublish: (
        candidate: ModelDocumentCandidate,
        tags: ModelDocTag[],
        title: string | undefined,
        notify: boolean,
        publish: PublishScope,
    ) => Promise<boolean>;
    /** Candidates offered as a newer revision of a document (`DocumentSearch.replaces`), by candidate id. */
    revisions: Partial<Record<ModelDocumentId, ModelDocumentId>>;
    /** Link a newer revision of a replaced document instead of re-publishing the replaced one. */
    onLinkRevision: (candidate: ModelDocumentCandidate, revisionId: ModelDocumentId) => Promise<boolean>;
    /** The human veto (`not-relevant`) or its reversal (`undefined`). */
    onSetNotRelevant: (candidate: ModelDocumentCandidate, notRelevant: boolean) => Promise<boolean>;
    /** Explicitly fetch an oversize candidate full-size (offer with the cap waived); it returns staged. */
    onBringIn: (candidate: ModelDocumentCandidate) => Promise<boolean>;
    /**
     * Per-page language analysis of the staged bytes (server-side). New candidates get it automatically;
     * this covers ones that predate the auto-trigger.
     */
    onAnalyzeLanguages: (docId: ModelDocumentId) => Promise<boolean>;
    /** Cut pages out of a candidate's staged bytes, in place — no second row joins the queue. */
    onApplyTrim: ApplyTrim;
    /** Restore the candidate's full staged PDF, discarding every cut made on it. */
    onRestoreTrim: RestoreTrim;
}

/**
 * The review pool: fetched + verified PDFs still awaiting a human call. Verifier-rejected ones are shown,
 * because the verdict is the AI's opinion, not the last word; confirmed ones are NOT — they've moved up
 * into the Documents card (unpublishing there returns them here). "Confirm as…" is the force-accept
 * ("actually, this IS the owner manual"), the reclassify-confirm ("I'll accept this as a Warranty
 * sheet"), AND the library-confirm (a correct Parts List gets recorded without ever reaching the app —
 * visibility follows from the chosen tags). Not Relevant stays the standing human veto for WRONG
 * documents, which the worker honors unconditionally — vetoed rows tuck under a collapsed panel.
 */
export const CandidatesCard: FC<CandidatesCardProps> = ({
    candidates,
    modelNumber,
    documents,
    coverageContext,
    saving,
    onPublish,
    revisions,
    onLinkRevision,
    onSetNotRelevant,
    onBringIn,
    onAnalyzeLanguages,
    onApplyTrim,
    onRestoreTrim,
}) => {
    const [vetoedOpen, setVetoedOpen] = useState(false);
    const [typeFilter, setTypeFilter] = useCandidateTypeFilter();
    const unconfirmed = sortByDocumentType(candidates.filter((c) => !documents[c.id]));
    const awaitingReview = unconfirmed.filter((c) => c.disposition !== 'not-relevant');
    const shown = typeFilter ? unconfirmed.filter((c) => c.verdict.classification === typeFilter) : unconfirmed;
    const active = shown.filter((c) => c.disposition !== 'not-relevant');
    const vetoed = shown.filter((c) => c.disposition === 'not-relevant');

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex flex-wrap items-center gap-2">
                    Candidates — Model
                    {/* The model number, stated where the judging happens: every verdict below is
                        "is this document about THIS number", and the reviewer needs it in view. */}
                    <span className="rounded-md bg-blue-100 px-2 py-0.5 font-mono text-base text-blue-800">
                        {modelNumber}
                    </span>
                </CardTitle>
                {/* What confirming MEANS now lives on the form's Classification hint, next to the decision. */}
                <CardDescription>
                    What the search fetched and judged, previewable from our staged copy — confirmed candidates move up
                    into Documents.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                {candidates.length === 0 && (
                    <div className="section-description">
                        Nothing fetched yet — run a search, or offer a URL or file above.
                    </div>
                )}
                {(awaitingReview.length > 0 || typeFilter) && (
                    <FilterPills
                        label="Type"
                        items={nonEmptyPills(
                            DOCUMENT_TYPE_ORDER,
                            DOC_TAG_LABELS,
                            countByType(awaitingReview),
                            typeFilter,
                        )}
                        selected={typeFilter}
                        allCount={awaitingReview.length}
                        onSelect={(key) => setTypeFilter(DOCUMENT_TYPE_ORDER.find((type) => type === key))}
                    />
                )}
                {candidates.length > 0 && active.length === 0 && (
                    <div className="section-description">
                        {typeFilter
                            ? `No ${DOC_TAG_LABELS[typeFilter]} candidates awaiting review.`
                            : 'Nothing awaiting review.'}
                    </div>
                )}
                {active.map((candidate) => (
                    <CandidateRow
                        key={candidate.id}
                        candidate={candidate}
                        coverageContext={coverageContext}
                        saving={saving}
                        onPublish={onPublish}
                        replaces={revisions[candidate.id]}
                        onLinkRevision={onLinkRevision}
                        onSetNotRelevant={onSetNotRelevant}
                        onBringIn={onBringIn}
                        onAnalyzeLanguages={onAnalyzeLanguages}
                        onApplyTrim={onApplyTrim}
                        onRestoreTrim={onRestoreTrim}
                    />
                ))}
                {vetoed.length > 0 && (
                    <div className="space-y-3 border-t pt-3">
                        <button
                            type="button"
                            onClick={() => setVetoedOpen((open) => !open)}
                            className="flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                        >
                            <ChevronRight
                                className={cn('h-3.5 w-3.5 transition-transform', vetoedOpen && 'rotate-90')}
                            />
                            Not relevant ({vetoed.length})
                        </button>
                        {vetoedOpen &&
                            vetoed.map((candidate) => (
                                <CandidateRow
                                    key={candidate.id}
                                    candidate={candidate}
                                    coverageContext={coverageContext}
                                    saving={saving}
                                    onPublish={onPublish}
                                    replaces={revisions[candidate.id]}
                                    onLinkRevision={onLinkRevision}
                                    onSetNotRelevant={onSetNotRelevant}
                                    onBringIn={onBringIn}
                                    onAnalyzeLanguages={onAnalyzeLanguages}
                                    onApplyTrim={onApplyTrim}
                                    onRestoreTrim={onRestoreTrim}
                                />
                            ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

/** The tag picker's order, owner's manual first: the order candidates list in and the type pills read in. */
const DOCUMENT_TYPE_ORDER = ModelDocTagSchema.options;

/** The type filter lives in the URL so it survives opening a document and coming back. */
function useCandidateTypeFilter(): [ModelDocTag | undefined, (type: ModelDocTag | undefined) => void] {
    const [searchParams] = useSearchParams();
    const setSearchParam = useSetSearchParam();
    const param = searchParams.get('candidateType');
    const typeFilter = DOCUMENT_TYPE_ORDER.find((type) => type === param);
    return [typeFilter, (type) => setSearchParam('candidateType', type)];
}

/** Stable, so newest-first still holds within each type. */
function sortByDocumentType(candidates: ModelDocumentCandidate[]): ModelDocumentCandidate[] {
    const rank = (candidate: ModelDocumentCandidate) => DOCUMENT_TYPE_ORDER.indexOf(candidate.verdict.classification);
    return [...candidates].sort((a, b) => rank(a) - rank(b));
}

function countByType(candidates: ModelDocumentCandidate[]): Partial<Record<ModelDocTag, number>> {
    const counts: Partial<Record<ModelDocTag, number>> = {};
    for (const { verdict } of candidates) {
        counts[verdict.classification] = (counts[verdict.classification] ?? 0) + 1;
    }
    return counts;
}

const IDENTITY_META: Record<'yes' | 'no' | 'unsure', { label: string; className: string }> = {
    yes: { label: 'Right product', className: 'bg-emerald-100 text-emerald-900' },
    no: { label: 'Wrong product', className: 'bg-rose-100 text-rose-900' },
    unsure: { label: 'Product unsure', className: 'bg-amber-100 text-amber-900' },
};

const CandidateRow: FC<{
    candidate: ModelDocumentCandidate;
    coverageContext: CoverageContext;
    saving: boolean;
    onPublish: CandidatesCardProps['onPublish'];
    replaces: ModelDocumentId | undefined;
    onLinkRevision: CandidatesCardProps['onLinkRevision'];
    onSetNotRelevant: CandidatesCardProps['onSetNotRelevant'];
    onBringIn: CandidatesCardProps['onBringIn'];
    onAnalyzeLanguages: CandidatesCardProps['onAnalyzeLanguages'];
    onApplyTrim: CandidatesCardProps['onApplyTrim'];
    onRestoreTrim: CandidatesCardProps['onRestoreTrim'];
}> = ({
    candidate,
    coverageContext,
    saving,
    onPublish,
    replaces,
    onLinkRevision,
    onSetNotRelevant,
    onBringIn,
    onAnalyzeLanguages,
    onApplyTrim,
    onRestoreTrim,
}) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [trimOpen, setTrimOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const { verdict } = candidate;
    const documentDates = describeDocumentDates(verdict.documentDates);
    const identity = IDENTITY_META[verdict.identityMatch];
    const isVetoed = candidate.disposition === 'not-relevant';
    const canAnalyze = canAnalyzeLanguages(candidate);
    const manualish = verdict.identityMatch === 'yes' && isManualishClassification(verdict.classification);

    // Confirming means reviewing: opening the form also opens the preview so the reviewer classifies
    // while reading (the whole point of the inline form). Closing the form leaves the preview up.
    const openConfirm = () => {
        setConfirmOpen(true);
        setPreviewOpen(true);
    };

    return (
        <div className={cn('space-y-2 rounded-md border p-3', isVetoed && 'opacity-60')}>
            <div className="flex flex-wrap items-center gap-2">
                {isVetoed && (
                    <Badge variant="secondary" className="border-transparent bg-violet-100 text-violet-900">
                        Not relevant
                    </Badge>
                )}
                {!isVetoed && manualish && (
                    <Badge variant="secondary" className="border-transparent bg-amber-100 text-amber-900">
                        Needs review
                    </Badge>
                )}
                {candidate.oversize ? (
                    <Badge variant="secondary" className="border-transparent bg-slate-200 text-slate-700">
                        Too large{candidate.byteSize ? ` (${Math.round(candidate.byteSize / 1024 / 1024)}MB)` : ''}
                    </Badge>
                ) : (
                    <>
                        <Badge variant="outline" className="capitalize">
                            {verdict.classification.replace(/-/g, ' ')}
                        </Badge>
                        <Badge variant="secondary" className={cn('border-transparent', identity.className)}>
                            {identity.label}
                        </Badge>
                    </>
                )}
                {!candidate.oversize && (
                    <span className="text-xs text-muted-foreground">
                        {Math.round(verdict.confidence * 100)}%{verdict.verifiedVia ? ` (${verdict.verifiedVia})` : ''}
                    </span>
                )}
                {candidate.trim && (
                    <Badge variant="secondary" className="border-transparent bg-purple-100 text-purple-900">
                        Trimmed to {formatPageRanges(candidate.trim.keepRanges)}
                    </Badge>
                )}
                {!candidate.trim && candidate.languageAnalysis && (
                    <LanguageAnalysisBadge
                        analysis={candidate.languageAnalysis}
                        light
                        saving={saving}
                        onReanalyze={canAnalyze && !isVetoed ? () => onAnalyzeLanguages(candidate.id) : undefined}
                    />
                )}
                <div className="ml-auto flex items-center gap-1">
                    {candidate.oversize && !isVetoed && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => void onBringIn(candidate)}
                            disabled={saving}
                        >
                            Bring it in
                        </Button>
                    )}
                    {candidate.stagedStorageUrl && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => setPreviewOpen((v) => !v)}>
                            {previewOpen ? 'Hide preview' : 'Preview'}
                        </Button>
                    )}
                    {/* A vetoed row offers exactly Preview + Restore — restore first, then confirm/analyze. */}
                    {isVetoed ? (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => void onSetNotRelevant(candidate, false)}
                            disabled={saving}
                        >
                            Restore
                        </Button>
                    ) : (
                        <>
                            {candidate.stagedStorageUrl &&
                                // While the inline form is open, the green lives on ITS confirm button —
                                // this trigger drops to secondary and becomes the way out.
                                (confirmOpen ? (
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setConfirmOpen(false)}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </Button>
                                ) : (
                                    <Button
                                        type="button"
                                        variant="success"
                                        size="sm"
                                        onClick={openConfirm}
                                        disabled={saving}
                                    >
                                        Confirm as…
                                    </Button>
                                ))}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" aria-label="Candidate actions">
                                        <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    {candidate.stagedStorageUrl && !candidate.oversize && (
                                        <>
                                            <DropdownMenuItem onSelect={() => setTrimOpen(true)}>
                                                <Scissors className="h-4 w-4" />
                                                Trim PDF…
                                            </DropdownMenuItem>
                                            {candidate.trim && (
                                                <DropdownMenuItem
                                                    disabled={saving}
                                                    onSelect={() => void onRestoreTrim('candidate', candidate.id)}
                                                >
                                                    <RotateCcw className="h-4 w-4" />
                                                    Restore full PDF
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuSeparator />
                                        </>
                                    )}
                                    <DropdownMenuItem
                                        variant="destructive"
                                        disabled={saving}
                                        onSelect={() => void onSetNotRelevant(candidate, true)}
                                    >
                                        <Ban className="h-4 w-4" />
                                        Not relevant
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </>
                    )}
                </div>
            </div>

            {verdict.docIdentity && <div className="text-xs text-foreground">{verdict.docIdentity}</div>}
            {verdict.reason && <div className="field-hint">{verdict.reason}</div>}
            {/* ONE muted meta line — everything secondary lives here or behind the disclosure. The trail,
                the shadow-gate readout, and the full URL list open on demand instead of stacking four
                always-visible strata under every row. A PASSING gate is the one glanceable exception. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                {(candidate.trails?.length || candidate.gate) && (
                    <button
                        type="button"
                        onClick={() => setDetailsOpen((open) => !open)}
                        className="flex items-center gap-1 font-medium transition-colors hover:text-foreground"
                    >
                        <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', detailsOpen && 'rotate-90')} />
                        {candidate.trails?.length
                            ? `Found via — ${candidate.trails[0]?.steps.length ?? 0} steps`
                            : 'Details'}
                    </button>
                )}
                {candidate.gate?.pass && (
                    <Badge
                        variant="secondary"
                        className="border-transparent bg-emerald-100 text-emerald-900"
                        title="Every auto-confirm shadow check passed — open the disclosure for the per-check basis."
                    >
                        would auto-confirm ✓
                    </Badge>
                )}
                {/* The document's own date sits immediately before ours on purpose: "dated May 2015,
                    seen Jul 2026" makes the distinction legible, where a lone "seen" date reads as the
                    document's. Absent whenever the PDF reported none. */}
                {documentDates && (
                    <span className="font-medium text-foreground/80" title={documentDates.title}>
                        {documentDates.label}
                    </span>
                )}
                <span title="When we fetched these bytes.">seen {new Date(candidate.triedAt).toLocaleString()}</span>
                {candidate.urls.slice(0, 2).map((url) => {
                    const where = describeUrl(url);
                    return (
                        <a
                            key={url}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 break-all underline-offset-4 hover:underline"
                        >
                            {where?.label}
                            <ExternalLink className="h-3 w-3" />
                        </a>
                    );
                })}
                {candidate.urls.length > 2 && <span>+{candidate.urls.length - 2} more</span>}
            </div>
            {detailsOpen && (
                <div className="space-y-3 rounded-md border bg-muted/30 p-3">
                    {candidate.trails && candidate.trails.length > 0 && <TrailList trails={candidate.trails} />}
                    {candidate.gate && <GateShadowRow gate={candidate.gate} />}
                    {candidate.urls.length > 2 && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            <span>All sources:</span>
                            {candidate.urls.map((url) => (
                                <a
                                    key={url}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="break-all underline-offset-4 hover:underline"
                                >
                                    {describeUrl(url)?.label}
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {/* Inline confirm form, right above the preview — fill it out while reviewing the PDF.
                Conditionally mounted so a fresh open never inherits another session's edits. */}
            {confirmOpen && (
                <div className="rounded-md border bg-muted/30 p-3">
                    <CandidateConfirmForm
                        candidate={candidate}
                        coverageContext={coverageContext}
                        saving={saving}
                        onPublish={onPublish}
                        replaces={replaces}
                        onLinkRevision={onLinkRevision}
                        onCancel={() => setConfirmOpen(false)}
                    />
                </div>
            )}
            {previewOpen && candidate.stagedStorageUrl && (
                <PdfPreview url={candidate.stagedStorageUrl} title={verdict.docIdentity ?? candidate.id} />
            )}
            <TrimPdfDialog
                open={trimOpen}
                onOpenChange={setTrimOpen}
                title={verdict.docIdentity ?? candidate.id}
                analysis={candidate.languageAnalysis}
                canAnalyze={canAnalyze}
                pageCount={
                    candidate.trim
                        ? countPagesInRanges(candidate.trim.keepRanges)
                        : candidate.languageAnalysis?.totalPages
                }
                trimmed={!!candidate.trim}
                saving={saving}
                outcome="These pages replace this candidate's staged PDF — same row, same verdict, nothing new to review. Nothing user-facing changes until you publish it, and “Restore full PDF” brings the whole thing back."
                submitLabel="Cut pages"
                onAnalyze={() => onAnalyzeLanguages(candidate.id)}
                onApply={(cut) => onApplyTrim('candidate', candidate.id, cut)}
            />
        </div>
    );
};

/**
 * The confirm form. The bytes may already be a document (published from another model): it then starts
 * from that document's brands and types, which a publish only ever adds to. Bytes offered as a newer
 * revision start from the replaced document's brands, types and rule, and publishing them replaces it.
 * Bytes a newer revision replaced offer that revision instead.
 */
const CandidateConfirmForm: FC<{
    candidate: ModelDocumentCandidate;
    coverageContext: CoverageContext;
    saving: boolean;
    onPublish: CandidatesCardProps['onPublish'];
    replaces: ModelDocumentId | undefined;
    onLinkRevision: CandidatesCardProps['onLinkRevision'];
    onCancel: () => void;
}> = ({ replaces, onLinkRevision, ...props }) => {
    const [existing, loadable] = useLoadableAtomValue(
        useMemo(() => modelDocumentState(props.candidate.id), [props.candidate.id]),
    );
    const [replaced, replacedLoadable] = useLoadableAtomValue(useMemo(() => modelDocumentState(replaces), [replaces]));
    if (loadable.state === 'loading' || (replaces && replacedLoadable.state === 'loading'))
        return <div className="field-hint">Checking whether these bytes are already a document…</div>;
    const stored = existing ? { brandIds: existing.brandIds, typeIds: existing.typeIds } : undefined;
    const revisionId = existing?.replacedBy;
    return (
        <div className="space-y-3">
            {revisionId && (
                <ReplacedNotice
                    revisionId={revisionId}
                    saving={props.saving}
                    onLink={() => onLinkRevision(props.candidate, revisionId)}
                />
            )}
            {replaced && (
                <div className="text-sm">
                    Offered as a newer revision of <DocumentLink id={replaced.id} doc={replaced} />: publishing it
                    replaces that document.
                </div>
            )}
            <CandidateConfirmFields
                {...props}
                stored={stored}
                storedRule={existing?.coverage}
                storedTags={existing?.tags}
                revisionOf={existing ? undefined : replaced}
                replaced={replaced}
            />
        </div>
    );
};

/** These bytes were replaced by a newer revision: link that one instead of re-publishing these. */
const ReplacedNotice: FC<{ revisionId: ModelDocumentId; saving: boolean; onLink: () => Promise<boolean> }> = ({
    revisionId,
    saving,
    onLink,
}) => {
    const [revision] = useLoadableAtomValue(useMemo(() => modelDocumentState(revisionId), [revisionId]));
    return (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-orange-200 bg-orange-50 p-2 text-sm text-orange-950">
            <span>
                A newer revision replaced these bytes: <DocumentLink id={revisionId} doc={revision} />.
            </span>
            <Button type="button" size="sm" variant="outline" disabled={saving} onClick={() => void onLink()}>
                Link the newest revision instead
            </Button>
        </div>
    );
};

/**
 * The confirm form's fields, with the coverage draft OWNED here so every open re-seeds from the verdict
 * current at that moment — the chips and the "Removed:" suggestions always describe the SAME extraction.
 * When this state lived on the row it outlived verdict refreshes (the worker re-observing the same bytes
 * merges a new verdict in place), so newly extracted tokens rendered under "Removed:" as if a human had
 * pruned them, and stale tokens stayed publishable.
 *
 * The model's own brand and type are locked into the scope while the publish links the model: dropping
 * one would flag the link the moment it is made. They unlock with the opt-out, when the reviewer files the
 * document under other models only.
 */
const CandidateConfirmFields: FC<{
    candidate: ModelDocumentCandidate;
    coverageContext: CoverageContext;
    stored: DocumentScope | undefined;
    /** The document's rule, when the bytes are already a document with one: a re-publish keeps it. */
    storedRule: DocumentCoverage | undefined;
    /** The tags the document already carries: a publish keeps them (`publishedTags`). */
    storedTags: ModelDocTag[] | undefined;
    /** The document these bytes were offered to replace: the form starts from its scope and rule. */
    revisionOf: ModelDocument | undefined;
    /** The document publishing these bytes replaces, whether or not the form starts from it. */
    replaced: ModelDocument | undefined;
    saving: boolean;
    onPublish: CandidatesCardProps['onPublish'];
    onCancel: () => void;
}> = ({
    candidate,
    coverageContext,
    stored,
    storedRule,
    storedTags,
    revisionOf,
    replaced,
    saving,
    onPublish,
    onCancel,
}) => {
    const { verdict } = candidate;
    const families = useAsyncAtomValue(equipmentTypeFamiliesState, []);
    const modelScope: DocumentScope = {
        brandIds: coverageContext.brandId ? [coverageContext.brandId] : [],
        typeIds: [coverageContext.typeId],
    };
    // One snapshot per open (mount): a mid-review verdict refresh must not reshuffle chips under the
    // reviewer's cursor; reopening the form picks up the fresh extraction.
    const [seed] = useState(() => ({
        // A rule already on file is what the publish keeps (`handlePublishCandidate`), so it is what the
        // preview shows; the verifier's extraction stays one click away under "Removed:".
        draft: seedRule(storedRule ?? revisionOf?.coverage) ?? coverageDraftFromExtraction(verdict),
        suggestions: coverageSuggestions(verdict),
        scope: mergeScopes(stored, revisionOf, modelScope),
    }));
    const [scope, setScope] = useState<DocumentScope>(seed.scope);
    const [coverage, setCoverage] = useState<CoverageDraft>(seed.draft);
    // Default OFF: the checkbox only ever renders once the rule on screen provably excludes this model, so
    // by then the reviewer has already said the document doesn't apply here. Nothing reads it while the
    // rule DOES cover the model — that link is the rule's own doing, not this flag's.
    const [linkThisModel, setLinkThisModel] = useState(false);
    const coversThisModel = coverageDraftCoversModel(coverage, scope, coverageContext, families);
    const linksThisModel = !coverageDraftActive(coverage) || coversThisModel || linkThisModel;

    const submit = (tags: ModelDocTag[], title: string | undefined, notify: boolean): Promise<boolean> => {
        const error = documentScopeError(scope);
        if (error) {
            toast.error(error);
            return Promise.resolve(false);
        }
        return onPublish(candidate, tags, title, notify, {
            scope,
            rule: coverageDraftActive(coverage) ? coverage : undefined,
            linkThisModel: linksThisModel,
            ...(replaced ? { replaces: replaced.id } : {}),
        });
    };

    return (
        <DocumentForm
            submitLabel={(tags) => (includesAppVisibleTag(tags) ? 'Publish to the app' : 'Confirm to library')}
            initialTags={suggestedTags(verdict)}
            saving={saving}
            notifiable
            submitVariant="success"
            blockedReason={(tags) =>
                coverageContext.brandId
                    ? replaced &&
                      supersessionRefusal({ tags: publishedTags(tags, { tags: storedTags ?? [] }) }, replaced)
                    : 'Link a brand to this model first: every document is filed under the brand it is for.'
            }
            scope={{
                value: scope,
                onChange: setScope,
                locked: mergeScopes(stored, linksThisModel ? modelScope : undefined),
                stored,
                spaceId: coverageContext.spaceId,
                saving,
            }}
            coverage={{
                value: coverage,
                onChange: setCoverage,
                suggestions: seed.suggestions,
                publishTarget: {
                    modelDisplay: coverageContext.modelDisplay,
                    covered: coversThisModel,
                    linkThisModel,
                    onLinkThisModelChange: setLinkThisModel,
                },
            }}
            onSubmit={submit}
            onCancel={onCancel}
        />
    );
};

function seedRule(rule: DocumentCoverage | undefined): CoverageDraft | undefined {
    return rule && { prefixes: [...rule.prefixes], ranges: [...(rule.ranges ?? [])] };
}

const GATE_CHECK_LABELS: Record<keyof CandidateGate['checks'], string> = {
    publisher: 'publisher',
    domainTier: 'domain',
    identityStrength: 'identity',
    confidence: 'confidence',
};

/**
 * SHADOW-MODE readout of the auto-confirm composite gate the worker stamped on this verdict — pure
 * visibility while the gate calibrates against human decisions; nothing acts on it. Hover a check for
 * its recorded basis.
 */
const GateShadowRow: FC<{ gate: CandidateGate }> = ({ gate }) => (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-muted-foreground" title={`Evaluated ${new Date(gate.evaluatedAt).toLocaleString()}`}>
            Auto-confirm gate (shadow, v{gate.gateRevision}): {gate.pass ? 'would pass' : 'would hold for review'}
        </span>
        {(Object.keys(GATE_CHECK_LABELS) as (keyof CandidateGate['checks'])[]).map((key) => {
            const check = gate.checks[key];
            return (
                <Badge
                    key={key}
                    variant="secondary"
                    title={check.detail}
                    className={cn(
                        'border-transparent',
                        check.pass ? 'bg-emerald-100 text-emerald-900' : 'bg-rose-100 text-rose-900',
                    )}
                >
                    {check.pass ? '✓' : '✗'} {GATE_CHECK_LABELS[key]}
                </Badge>
            );
        })}
    </div>
);
